<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue';
import { router } from '@inertiajs/vue3';

const compareList = ref([]);
const isMounted = ref(false);
const STORAGE_KEY = 'pricecompare_compare';

function loadList() {
    if (typeof window === 'undefined') return;
    try {
        const data = localStorage.getItem(STORAGE_KEY);
        compareList.value = data ? JSON.parse(data) : [];
    } catch {
        compareList.value = [];
    }
}

function saveList(list) {
    if (typeof window === 'undefined') return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    window.dispatchEvent(new CustomEvent('compare-updated'));
}

function removeProduct(slug) {
    const updated = compareList.value.filter(item => item.slug !== slug);
    compareList.value = updated;
    saveList(updated);
}

function clearAll() {
    compareList.value = [];
    saveList([]);
}

function goToCompare() {
    const slugs = compareList.value.map(item => item.slug).join(',');
    router.visit('/sammenlign?products=' + slugs);
}

function getProductImage(item) {
    return item.image || '/images/static/placeholder.png';
}

const showBar = computed(() => {
    return isMounted.value && compareList.value.length >= 2;
});

const showBarAny = computed(() => {
    return isMounted.value && compareList.value.length >= 1;
});

function onCompareUpdated() {
    loadList();
}

onMounted(() => {
    isMounted.value = true;
    loadList();
    window.addEventListener('compare-updated', onCompareUpdated);
});

onUnmounted(() => {
    if (typeof window !== 'undefined') {
        window.removeEventListener('compare-updated', onCompareUpdated);
    }
});
</script>

<template>
    <Teleport to="body" v-if="isMounted">
        <Transition name="compare-bar">
            <div v-if="showBarAny" class="compare-bar-wrapper">
                <div class="container-fluid">
                    <div class="d-flex align-items-center justify-content-between gap-3">
                        <div class="d-flex align-items-center gap-2 compare-bar-products">
                            <div
                                v-for="item in compareList"
                                :key="item.slug"
                                class="compare-bar-item"
                            >
                                <img
                                    :src="getProductImage(item)"
                                    :alt="item.name"
                                    class="compare-bar-thumb"
                                />
                                <button
                                    class="compare-bar-remove"
                                    @click="removeProduct(item.slug)"
                                    :title="'Fjern ' + item.name"
                                >
                                    <font-awesome-icon :icon="['fas', 'xmark']" />
                                </button>
                            </div>
                            <span class="text-white small ms-2">
                                {{ compareList.length }} produkt{{ compareList.length !== 1 ? 'er' : '' }} valgt
                            </span>
                        </div>
                        <div class="d-flex gap-2">
                            <button
                                v-if="showBar"
                                class="btn btn-success btn-sm"
                                @click="goToCompare"
                            >
                                <font-awesome-icon :icon="['fas', 'scale-balanced']" class="me-1" />
                                Sammenlign {{ compareList.length }} produkter
                            </button>
                            <span v-else class="text-white-50 small align-self-center">
                                V&aelig;lg mindst 2 produkter
                            </span>
                            <button
                                class="btn btn-outline-light btn-sm"
                                @click="clearAll"
                            >
                                Ryd
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<style scoped>
.compare-bar-wrapper {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 1050;
    background-color: rgba(33, 37, 41, 0.95);
    backdrop-filter: blur(8px);
    padding: 0.75rem 1rem;
    box-shadow: 0 -2px 12px rgba(0, 0, 0, 0.3);
}

.compare-bar-products {
    overflow-x: auto;
    flex-shrink: 1;
}

.compare-bar-item {
    position: relative;
    flex-shrink: 0;
    width: 52px;
    height: 52px;
    background: #fff;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 4px;
}

.compare-bar-thumb {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
}

.compare-bar-remove {
    position: absolute;
    top: -6px;
    right: -6px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    border: none;
    background-color: #dc3545;
    color: #fff;
    font-size: 0.6rem;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 0;
    line-height: 1;
}

.compare-bar-remove:hover {
    background-color: #bb2d3b;
}

/* Transition */
.compare-bar-enter-active,
.compare-bar-leave-active {
    transition: transform 0.3s ease, opacity 0.3s ease;
}

.compare-bar-enter-from,
.compare-bar-leave-to {
    transform: translateY(100%);
    opacity: 0;
}
</style>
